<template>
  <b-modal
    id="mobile-how-modal"
    v-model="show"
    ok-only
    centered
    size="lg"
    footer-class="border-top d-none d-sm-block"
    header-class="d-none d-sm-block"
    :ok-title="hebrew ? 'סגור' : 'Close'"
    ok-variant="secondary"
  >
    <template slot="modal-header">{{
      hebrew ? "איך זה עובד" : "How it works"
    }}</template>
    <div
      class="mobile-modal-top bg-secondary position-fixed w-100 d-block d-sm-none"
    >
      <div class="row">
        <div class="col-8">
          <h3>{{ hebrew ? "איך זה עובד" : "How it works" }}</h3>
        </div>
        <div
          @click="show = false"
          class="close-icon col-4"
          v-bind:class="{ 'text-left': hebrew, 'text-right': !hebrew }"
        >
          <i class="fas fa-times"></i>
        </div>
      </div>
    </div>
    <div class="how-to-text" v-if="hebrew">
      <p>
        הכלי לאיתור מקביליות נועד לאתר טקסטים המקבילים לטקסט המקור שהזנתם. הכלי
        מסוגל לאתר פסקאות מקבילות גם כאשר הפסקאות שונות זו מזו בנטיות הדקדוקיות
        של המילים, באותיות השימוש, וגם כאשר מילים אחדות הושמטו, נוספו, או
        הוחלפו.
      </p>
      <p>
        הכלי מחפש מקביליות לכל אורך מאגר הטקסטים שבאתר ספריא ובאתר בן-יהודה.
        הכלי מציג מקבילות שיש להן לפחות 10 מילים במשותף (ולו באופן חלקי) עם טקסט
        הקלט.
      </p>
      <p>
        לאחר איתור המקבילות, הכלי יאפשר לכם להשוות בין הפסקאות המקבילות, כדי
        להקל עליכם בזיהוי ההבדלים העדינים שביניהן.
      </p>
      <p>
        להלן דוגמא:
        <br />
        טקסט הקלט הוא הספר "ארי נוהם" מאת יהודה אריה ממודינא. כאן מודינא מצטט
        משפט מאת ר' קורדווירו; אבל הוא לא מציין את שם הספר, אלא מסתפק באמירה
        שהמשפט נמצא "בחקרו". כלי המקבילות של דיקטה מגלה שמדובר בספר "פרדס
        רימונים", והפסקה המלאה מובאת מצד שמאל לרווחת הקורא.
      </p>
      <img
        class="d-block m-auto border"
        src="@/assets/img/parallels.jpg"
        alt="parallels example"
      />
      <p class="mt-3">
        שימו לב שמדובר בהתאמה חלקית, כלומר, המקבילות אינן זהות לחלוטין בלשונן:
        למשל, כאן 'זה לשונו' וכאן 'ונאמר'. אם נקליק על הכפתור "השוואת גרסאות",
        נוכל לראות את הפסקאות מסודרות מילה-מילה, כאשר השינויים שביניהם מובלטים
        בצבע:
      </p>
      <img
        class="d-block m-auto border"
        src="@/assets/img/synopsis.jpg"
        alt="synopsis example"
      />
      <p class="mt-3">
        כדי לסנן את התוצאות לפי ספרים יש ללחוץ על גלגל השיניים, הממוקם בשורת
        הכותרת של המקבילות. בכרטיסה הראשונה הנקראת ״טבלה״ תוכלו להסיר את הסמן
        מעל הספרים מהם אתם לא מעוניינים לראות תוצאות. בכרטיסה ״ציר הזמן״ יוצגו
        הספרים השונים בפריסה על ציר הזמן. בכרטיסייה ״מפה״ יוצגו הספרים בפריסה על
        המפה.
      </p>
      <img
        class="d-block m-auto border"
        src="@/assets/img/filter.jpg"
        alt="filter example"
      />
      <p class="mt-3">
        לפרטים מלאים על אודות האלגוריתם שעומד מאחורי הכלי לאיתור מקבילות, ניתן
        לקרוא כאן:
      </p>
      <p class="text-left pl-2" dir="ltr">
        Avi Shmidman, Moshe Koppel, and Ely Porat, “Identification of Parallel
        Passages Across a Large Hebrew/Aramaic Corpus,” Journal of Data Mining
        and Digital Humanities, Special Issue on Computer-Aided Processing of
        Intertextuality in Ancient Languages, March 2018:
        <a href="https://arxiv.org/abs/1602.08715" target="_blank"
          >https://arxiv.org/abs/1602.08715</a
        >
      </p>
    </div>
    <div v-else class="how-to-text">
      <p>
        The Parallels finder tool is designed to find texts that are parallel to
        an entered source text. The tool is able to locate parallel paragraphs
        even when the paragraphs differ in grammatical structure or word choice,
        in spelling, and even when words are omitted, added, or replaced.
      </p>
      <p>
        The tool scans the Sefaria and Ben-Yehuda texts for parallels and
        displays results that have at least 10 words in common (even partially)
        with the input text.
      </p>
      <p>
        After locating the parallels, the tool will allow you to compare the
        parallel paragraphs, making it easier to identify the subtle differences
        between them.
      </p>
      <p>
        In the example below, the input text is the book "ארי נוהם" by Yehuda
        Aryeh of Modina. Here Modina quotes a sentence by R. Cordaviro; But he
        does not specify the name of the book. Dicta's Parallels finder tool
        reveals that the book is named "פרדס רימונים". The full paragraph from
        the book is displayed for the convenience of the user.
      </p>
      <img
        class="d-block m-auto border"
        src="@/assets/img/parallels.jpg"
        alt="parallels example"
      />
      <p class="mt-3">
        Note that this is a partial match, that is, the parallels are not
        completely identical in their language: for example, in the source 'זה
        לשונו' appears, and in the results we see, 'ונאמר'. Clicking on the
        'Synopsis' button displays the paragraphs arranged word by word, with
        highlighted differences:
      </p>
      <img
        class="d-block m-auto border"
        src="@/assets/img/synopsis.jpg"
        alt="synopsis example"
      />
      <p class="mt-3">
        To filter the results by books, click on the gear icon, located in the
        title line of the parallels. In the Table tab, uncheck books to exclude
        them from the results. In the Timeline tab, the various books will be
        displayed in a timeline layout based on the year they were printed. In
        the Map tab, the books will be mapped according to the location where
        they were published.
      </p>
      <img
        class="d-block m-auto border"
        src="@/assets/img/filter.jpg"
        alt="filter example"
      />
      <p class="mt-3">
        For full details about the algorithm behind the tool, please see the
        below link:
      </p>
      <p>
        Avi Shmidman, Moshe Koppel, and Ely Porat, “Identification of Parallel
        Passages Across a Large Hebrew/Aramaic Corpus,” Journal of Data Mining
        and Digital Humanities, Special Issue on Computer-Aided Processing of
        Intertextuality in Ancient Languages, March 2018:
        <a href="https://arxiv.org/abs/1602.08715" target="_blank"
          >https://arxiv.org/abs/1602.08715</a
        >
      </p>
    </div>
  </b-modal>
</template>
<script>
export default {
  name: "about-dialog",
  props: ["value"],
  computed: {
    hebrew() {
      return this.$settings.hebrew
    },
    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },
  },
}
</script>
<style lang="scss">
@media only screen and (max-width: 767px) {
  #mobile-how-modal {
    img {
      max-width: 100%;
      height: auto;
    }

    -webkit-overflow-scrolling: touch;
    direction: ltr;
    text-align: left;

    .modal-dialog {
      margin: 0 !important;
    }

    .modal-content {
      border-radius: 0 !important;
      height: 100vh;
      overflow-y: scroll;
      overflow-x: hidden;
      background: white;
    }

    .mobile-modal-top {
      height: 50px;
      top: 0;
      right: 0;
      padding: 15px;
      z-index: 1;

      h3 {
        text-decoration: none;
      }
    }

    .how-to-text {
      padding-top: 50px;
      max-height: 88vh;
      overflow-y: scroll;
      overflow-x: hidden;
    }

    @media only screen and (max-width: 667px) {
      .how-to-text {
        max-height: 83vh;
      }
    }
  }

  .he #mobile-how-modal {
    text-align: right;
    direction: rtl;
  }
}
</style>
<style scoped lang="scss">
.how-to-text {
  max-height: 70vh;
  overflow: auto;
}
</style>

<style lang="scss">
@media only screen and (min-width: 1280px) {
  #mobile-how-modal {
    .modal-lg,
    .modal-xl {
      max-width: 1200px;
    }
  }
}
</style>
